import { useCallback, useState } from "react";
import { useTellescopeMutation } from "../use-tellescope-mutation";
import { useTellescopeQueryClient } from "../use-tellescope-query-client";
import type { ParamsForMethod, ReturnForMethod } from "../../types";
import type { SupportedSession } from "../../types";
import { useTellescopeSession } from "../../context";

/**
 * The current stage of the file upload process
 */
export type FileUploadStage = "idle" | "preparing" | "uploading" | "confirming" | "success" | "error";

/**
 * Configuration options for the useUploadTellescopeFile hook
 */
export interface FileUploadOptions {
  /**
   * Whether to automatically call confirm_file_upload after successful upload.
   * @default true
   */
  autoConfirm?: boolean;

  /**
   * Whether to fetch and return the download URL after successful upload.
   * @default false
   */
  returnDownloadURL?: boolean;

  /**
   * Maximum file size in bytes. If specified, files exceeding this size will be rejected.
   */
  maxFileSize?: number;

  /**
   * Array of allowed MIME types. If specified, only files matching these types will be accepted.
   * @example ["image/jpeg", "image/png", "application/pdf"]
   */
  allowedFileTypes?: string[];

  /**
   * Optional session to use for the API calls (uses context session if not provided)
   */
  session?: SupportedSession | null;
  prepareMutationOptions?: Omit<
    Parameters<typeof useTellescopeMutation<"files", "prepare_file_upload">>[0],
    "model" | "method"
  >;
  confirmMutationOptions?: Omit<
    Parameters<typeof useTellescopeMutation<"files", "confirm_file_upload">>[0],
    "model" | "method"
  >;
}

/**
 * Parameters for the file upload (matches prepare_file_upload parameters)
 */
export type FileUploadParams = ParamsForMethod<"files", "prepare_file_upload">;

/**
 * Response from prepare_file_upload (includes file record and presigned upload info)
 */
type PrepareFileUploadResponse = ReturnForMethod<"files", "prepare_file_upload">;

/**
 * The file record type from the Tellescope SDK
 */
type FileRecord = PrepareFileUploadResponse extends { file: infer F } ? F : never;

/**
 * Extended file record that includes download URL when returnDownloadURL is enabled
 */
type FileRecordWithURL = FileRecord & { downloadURL?: string };

/**
 * S3 presigned post structure for uploading files
 */
interface S3PresignedPost {
  url: string;
  fields: {
    "Content-Type": string;
    key: string;
    bucket: string;
    "X-Amz-Algorithm": string;
    "X-Amz-Credential": string;
    "X-Amz-Date": string;
    Policy: string;
    "X-Amz-Signature": string;
  };
}

/**
 * Return type for the useUploadTellescopeFile hook
 */
export interface UseFileUploadReturn {
  /**
   * Upload a file (fire-and-forget style, doesn't return anything)
   */
  uploadFile: (params: FileUploadParams, file: File | Blob) => void;

  /**
   * Upload a file (async style, returns the file record with optional downloadURL)
   */
  uploadFileAsync: (params: FileUploadParams, file: File | Blob) => Promise<FileRecordWithURL>;

  /**
   * Current stage of the upload process
   */
  stage: FileUploadStage;

  /**
   * Upload progress percentage (0-100, only relevant during 'uploading' stage)
   */
  progress: number;

  /**
   * Error from the prepare stage (if any)
   */
  prepareError: Error | null;

  /**
   * Error from the upload stage (if any)
   */
  uploadError: Error | null;

  /**
   * Error from the confirm stage (if any)
   */
  confirmError: Error | null;

  /**
   * Error from file validation (if any)
   */
  validationError: Error | null;

  /**
   * The file record (available after successful prepare)
   */
  file: FileRecord | undefined;

  /**
   * The download URL for the uploaded file (available after successful upload if returnDownloadURL is enabled)
   */
  downloadURL: string | undefined;

  /**
   * Whether any stage is currently loading
   */
  isLoading: boolean;

  /**
   * Reset the hook state back to idle
   */
  reset: () => void;
}

/**
 * A convenient hook for uploading files to Tellescope that wraps the 3-step process:
 * 1. prepare_file_upload - Get presigned S3 URL and create file record
 * 2. Upload to S3 - Upload the file using the presigned URL
 * 3. confirm_file_upload - Mark the file as confirmed (triggers webhooks)
 *
 * This hook provides full progress tracking and stage-specific error handling,
 * while keeping the API similar to standard tanstack-query mutations.
 *
 * @param options - Configuration options for the upload behavior
 *
 * @example
 * ```tsx
 * function FileUploader() {
 *   const { uploadFileAsync, stage, progress, prepareError, uploadError, confirmError, validationError } = useUploadTellescopeFile({
 *     maxFileSize: 5 * 1024 * 1024, // 5MB limit
 *     allowedFileTypes: ["image/jpeg", "image/png", "image/webp"]
 *   });
 *
 *   const handleUpload = async (file: File) => {
 *     try {
 *       const fileRecord = await uploadFileAsync({
 *         name: file.name,
 *         size: file.size,
 *         type: file.type,
 *         enduserId: "user-123",
 *       }, file);
 *       console.log("Upload complete!", fileRecord);
 *     } catch (error) {
 *       console.error("Upload failed:", error);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <input type="file" onChange={(e) => handleUpload(e.target.files[0])} />
 *       <div>Stage: {stage}</div>
 *       {stage === 'uploading' && <div>Progress: {progress}%</div>}
 *       {validationError && <div>Validation error: {validationError.message}</div>}
 *       {prepareError && <div>Prepare error: {prepareError.message}</div>}
 *       {uploadError && <div>Upload error: {uploadError.message}</div>}
 *       {confirmError && <div>Confirm error: {confirmError.message}</div>}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Manual confirmation control
 * function ManualConfirmUploader() {
 *   const { uploadFileAsync, stage, file } = useUploadTellescopeFile({ autoConfirm: false });
 *   const confirmMutation = useTellescopeMutation({ model: "files", method: "confirm_file_upload" });
 *
 *   const handleUpload = async (file: File) => {
 *     const fileRecord = await uploadFileAsync({ name: file.name, size: file.size, type: file.type }, file);
 *     // File is uploaded but not confirmed yet
 *     // Manually confirm when ready
 *     await confirmMutation.mutateAsync({ id: fileRecord.id });
 *   };
 * }
 * ```
 */
export const useUploadTellescopeFile = (options: FileUploadOptions = {}): UseFileUploadReturn => {
  const {
    autoConfirm = true,
    returnDownloadURL = false,
    maxFileSize,
    allowedFileTypes,
    session: providedSession,
    prepareMutationOptions,
    confirmMutationOptions,
  } = options;
  const session = useTellescopeSession(providedSession);
  const queryClient = useTellescopeQueryClient();

  // State management
  const [stage, setStage] = useState<FileUploadStage>("idle");
  const [progress, setProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [validationError, setValidationError] = useState<Error | null>(null);
  const [file, setFile] = useState<FileRecord | undefined>(undefined);
  const [downloadURL, setDownloadURL] = useState<string | undefined>(undefined);

  // Mutation for preparing the file upload
  const prepareMutation = useTellescopeMutation({
    model: "files",
    method: "prepare_file_upload",
    session: session ?? undefined,
    ...prepareMutationOptions,
  });

  // Mutation for confirming the file upload
  const confirmMutation = useTellescopeMutation({
    model: "files",
    method: "confirm_file_upload",
    session: session ?? undefined,
    ...confirmMutationOptions,
  });

  // Reset function
  const reset = useCallback(() => {
    setStage("idle");
    setProgress(0);
    setUploadError(null);
    setValidationError(null);
    setFile(undefined);
    setDownloadURL(undefined);
    prepareMutation.reset();
    confirmMutation.reset();
  }, [prepareMutation, confirmMutation]);

  // File validation function
  const validateFile = useCallback(
    (fileBlob: File | Blob): Error | null => {
      // Check file size
      if (maxFileSize && fileBlob.size > maxFileSize) {
        return new Error(
          `File size (${Math.round((fileBlob.size / 1024 / 1024) * 100) / 100}MB) exceeds maximum allowed size (${
            Math.round((maxFileSize / 1024 / 1024) * 100) / 100
          }MB)`
        );
      }

      // Check file type (only for File objects, Blob doesn't have type property)
      if (allowedFileTypes && fileBlob instanceof File) {
        if (!allowedFileTypes.includes(fileBlob.type)) {
          return new Error(
            `File type "${fileBlob.type}" is not allowed. Allowed types: ${allowedFileTypes.join(", ")}`
          );
        }
      }

      return null;
    },
    [maxFileSize, allowedFileTypes]
  );

  // Upload to S3 function
  const uploadToS3 = useCallback(async (presigned: S3PresignedPost, fileBlob: File | Blob): Promise<void> => {
    setStage("uploading");
    setProgress(0);
    setUploadError(null);

    try {
      const formData = new FormData();

      // Append all presigned fields first
      Object.entries(presigned.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Append file LAST (AWS requirement)
      formData.append("file", fileBlob);

      // Upload to S3 with progress tracking using XMLHttpRequest
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            setProgress(percentCompleted);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload to S3 failed"));
        });

        xhr.addEventListener("abort", () => {
          reject(new Error("Upload to S3 was aborted"));
        });

        xhr.open("POST", presigned.url);
        xhr.send(formData);
      });

      setProgress(100);
    } catch (error) {
      const uploadErr = error instanceof Error ? error : new Error("Upload to S3 failed");
      setUploadError(uploadErr);
      setStage("error");
      throw uploadErr;
    }
  }, []);

  // Main upload function (async version)
  const uploadFileAsync = useCallback(
    async (params: FileUploadParams, fileBlob: File | Blob): Promise<FileRecordWithURL> => {
      try {
        // Stage 0: Validate the file
        const validationErr = validateFile(fileBlob);
        if (validationErr) {
          setValidationError(validationErr);
          setStage("error");
          throw validationErr;
        }

        // Stage 1: Prepare the file upload
        setStage("preparing");
        setProgress(0);
        setUploadError(null);
        setValidationError(null);

        const prepareResponse = await prepareMutation.mutateAsync(params);
        const fileRecord = prepareResponse.file;
        const presignedUpload = prepareResponse.presignedUpload as S3PresignedPost;

        setFile(fileRecord);

        // Stage 2: Upload to S3
        await uploadToS3(presignedUpload, fileBlob);

        // Stage 3: Confirm the upload (if autoConfirm is enabled)
        if (autoConfirm) {
          setStage("confirming");
          await confirmMutation.mutateAsync({ id: fileRecord.id });
        }

        // Stage 4: Fetch download URL (if returnDownloadURL is enabled)
        let downloadURLResult: string | undefined;
        if (returnDownloadURL && fileRecord.secureName) {
          const { downloadURL: url } = await queryClient.fetchQuery({
            model: "files",
            method: "file_download_URL",
            request: { secureName: fileRecord.secureName },
          });
          setDownloadURL(url);
          downloadURLResult = url;
        }

        setStage("success");
        return { ...fileRecord, downloadURL: downloadURLResult };
      } catch (error) {
        setStage("error");
        throw error;
      }
    },
    [prepareMutation, confirmMutation, uploadToS3, autoConfirm, returnDownloadURL, queryClient, validateFile]
  );

  // Fire-and-forget version
  const uploadFile = useCallback(
    (params: FileUploadParams, fileBlob: File | Blob) => {
      uploadFileAsync(params, fileBlob).catch((error) => {
        console.error("File upload failed:", error);
      });
    },
    [uploadFileAsync]
  );

  // Determine loading state
  const isLoading = stage === "preparing" || stage === "uploading" || stage === "confirming";

  return {
    uploadFile,
    uploadFileAsync,
    stage,
    progress,
    prepareError: prepareMutation.error,
    uploadError,
    confirmError: confirmMutation.error,
    validationError,
    file,
    downloadURL,
    isLoading,
    reset,
  };
};
